import React, { memo, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classnames from 'classnames';
import { Advice, Modal, Text } from '@Sage/hoi-poi-ui';
import { FiltersProvider, useHorizontalResize } from '@Sage/ssm-web5';
import { EntityFiltersActions } from 'actions';
import Context from 'managers/Context';
import { RESIZE_LAYOUT } from 'constants/Constants';
import { PRODUCTS, SALESORDERS } from 'constants/Entities';
import { getProductFolders } from 'services/ProductsService';

import AbsoluteError from 'components/AbsoluteError';
import AbsoluteLoader from 'components/AbsoluteLoader';
import TreeHoi from 'components/TreeHoi';

import DetailLayout from 'containers/components/layouts/ContentDetailLayout';
import FreeTrialWarning from 'containers/components/FreeTrialWarning';
import NewEntityFilters from 'containers/components/NewEntityFilters';

import { getLiteral } from 'utils/getLiteral';
import { findPath, setFolderActive } from 'utils/treeNavigation';
import { subscribe } from 'lib/EventBuser';
import { UPDATE_PRODUCTS_MODAL_QUANTITY } from 'lib/events';

import AddProductsTable from './AddProductsTable';
import ProductsMenuHeader from './ProductsMenuHeader';
import Summary from './Summary';
import CONFIG from './TableConfig';

import './styles.scss';

const PRODUCTS_FILTER_KEY = 'salesOrdersAddProductsModal';
const PAGE_SIZE = 50;

const {
    DEFAULT_WIDTH: DEFAULT_TREE_WIDTH,
    MIN_WIDTH: MIN_TREE_WIDTH,
    MIN_PERCENTAGE: MIN_TREE_PERCENTAGE,
    MAX_PERCENTAGE: MAX_TREE_PERCENTAGE,
    WIDTH_KEY: TREE_WIDTH_STORAGE_KEY,
} = RESIZE_LAYOUT.ADD_PRODUCTS_TREE;

const initialState = {
    onConfirm: null,
    isOpen: false,
    isLoading: true,
    isLoadingProducts: false,
    error: false,
    errorProducts: false,
    folderTree: null,
    path: [],
    currentFolder: null,
    searchText: '',
};

function cloneFolderTree(node) {
    if (!node) return null;
    return {
        ...node,
        children: node.children?.map(cloneFolderTree) || [],
    };
}

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...initialState,
                onConfirm: action.onConfirm,
                isOpen: true,
                error: false,
                folderTree: state.folderTree,
                currentFolder: -1,
                path: [{ id: -1, text: getLiteral('title_products') }],
            };
        case 'setCurrentFolder':
            const path =
                findPath(state.folderTree, action.id)?.map((item) => ({
                    id: item.id,
                    text: item.name,
                })) || [];

            const updatedFolderTree = cloneFolderTree(state.folderTree);
            setFolderActive(updatedFolderTree, action.id);
            return {
                ...state,
                currentFolder: action.id,
                path,
                folderTree: updatedFolderTree,
            };
        case 'setFolders':
            const newFolderTree = cloneFolderTree(action.folderTree);
            setFolderActive(newFolderTree, state.currentFolder);
            return {
                ...state,
                folderTree: newFolderTree,
                isLoading: false,
                error: false,
            };
        case 'setLoadingProducts':
            return {
                ...state,
                isLoadingProducts: action.loading,
            };
        case 'close':
            return { ...state, isOpen: false };
        case 'reset':
            return { ...initialState, folderTree: state.folderTree };
        case 'changeSearchInput':
            return {
                ...state,
                searchText: action.value,
            };
        case 'setError':
            return {
                ...state,
                isLoading: false,
                error: action.error,
            };
        case 'setErrorProducts':
            return {
                ...state,
                errorProducts: action.error,
                isLoadingProducts: false,
            };
        default:
            throw new Error('No action provided');
    }
}

const AddProductsModal = memo(
    ({
        onRef,
        idRate,
        idCompany,
        idEnvironment,
        idCurrency,
        currencySymbol,
        quoteProductByEnvironment,
        onAddProducts,
    }) => {
        const filterIdEnvironment = quoteProductByEnvironment ? idEnvironment : '-1';
        const addedProducts = useRef([]);
        const quantityMap = useRef({});
        const [state, dispatch] = useReducer(reducer, initialState);
        const reduxDispatch = useDispatch();
        const showFilters = useSelector(
            (reduxState) => reduxState.entityFilters[PRODUCTS_FILTER_KEY]?.show || false,
        );

        const contentContainerRef = useRef(null);
        const treeSidebarRef = useRef(null);
        const [treeWidth, setTreeWidth] = useState(() => {
            const stored = localStorage.getItem(TREE_WIDTH_STORAGE_KEY);
            return stored || `${DEFAULT_TREE_WIDTH}px`;
        });

        const handleTreeResize = useCallback(({ percentageList }) => {
            setTreeWidth(`${percentageList}%`);
        }, []);

        const handleTreeResizeEnd = useCallback(({ percentageList }) => {
            const newWidth = `${percentageList}%`;
            localStorage.setItem(TREE_WIDTH_STORAGE_KEY, newWidth);
        }, []);

        const isContentReady = state.isOpen && !state.isLoading && !state.error;

        useHorizontalResize({
            targetRef: treeSidebarRef,
            containerRef: contentContainerRef,
            minWidth: MIN_TREE_WIDTH,
            minPercentage: MIN_TREE_PERCENTAGE,
            maxPercentage: MAX_TREE_PERCENTAGE,
            onResize: handleTreeResize,
            onResizeEnd: handleTreeResizeEnd,
            enabled: isContentReady,
        });

        const reduxProductsFilters = useSelector(
            (reduxState) => reduxState.entityFilters[PRODUCTS_FILTER_KEY]?.filters || {},
        );

        const filtersHash = useMemo(
            () => JSON.stringify(reduxProductsFilters),
            [reduxProductsFilters],
        );

        // Ref to keep track of current params for lazy load
        const currentParamsRef = useRef({
            folderId: -1,
            searchText: '',
            filters: {},
        });

        // Update params ref when they change
        useEffect(() => {
            currentParamsRef.current = {
                folderId: state.currentFolder,
                searchText: state.searchText,
                filters: reduxProductsFilters,
            };
        }, [state.currentFolder, state.searchText, reduxProductsFilters]);

        // initTable callback for lazy load - called by SectionList when more data is needed
        const initTable = useCallback(
            (pageSize, startRow) => {
                const { folderId, searchText, filters } = currentParamsRef.current;

                return new Promise((resolve, reject) => {
                    Context.entityManager
                        .getEntitiesManager(SALESORDERS)
                        .getSalesOrderModalProducts(
                            folderId,
                            searchText,
                            idRate,
                            idCompany,
                            filterIdEnvironment,
                            filters,
                            startRow,
                            pageSize,
                        )
                        .then((result) => {
                            // Filter out folders - only show products
                            const products =
                                result?.products?.filter((item) => !item.isProductFolder) || [];
                            const total = result?.total || 0;

                            resolve({ entities: products, total, offset: startRow });
                        })
                        .catch((error) => {
                            console.error(error);
                            reject(error);
                        });
                });
            },
            [idCompany, filterIdEnvironment, idRate],
        );

        // getTotal callback for lazy load - called by SectionList to get the total count
        const getTotal = useCallback(() => {
            const { folderId, searchText, filters } = currentParamsRef.current;

            return Context.entityManager
                .getEntitiesManager(SALESORDERS)
                .getSalesOrderModalProductsCount(
                    folderId,
                    searchText,
                    filterIdEnvironment,
                    filters,
                );
        }, [filterIdEnvironment]);

        const getFolders = useCallback(() => {
            const shouldFilterByEnvironment = filterIdEnvironment && filterIdEnvironment !== '-1';
            const environmentId = shouldFilterByEnvironment
                ? parseInt(filterIdEnvironment, 10)
                : null;

            function parseTreeFolder(childs) {
                return childs
                    .filter((child) => {
                        if (!shouldFilterByEnvironment) return true;
                        return (
                            !child.EnvironmentsList?.length ||
                            child.EnvironmentsList.includes(environmentId)
                        );
                    })
                    .map((child) => {
                        return {
                            id: child.Id,
                            name: child.FamilyName,
                            children: child.ChildFamily?.length
                                ? parseTreeFolder(child.ChildFamily)
                                : [],
                        };
                    });
            }

            getProductFolders({ idParent: -1 })
                .then((data) => {
                    let folders = [];
                    if (data?.data) folders = parseTreeFolder(data?.data);

                    dispatch({
                        type: 'setFolders',
                        folderTree: {
                            id: -1,
                            name: getLiteral('title_products'),
                            toggled: true,
                            children: folders,
                        },
                    });
                })
                .catch((e) => {
                    console.error(e);
                    dispatch({
                        type: 'setError',
                        error: 'folders-error',
                    });
                });
        }, [filterIdEnvironment]);

        // Ref for the table to refresh it when needed
        const tableRef = useRef(null);

        useEffect(() => {
            onRef &&
                onRef({
                    open() {
                        dispatch({ type: 'init' });
                        quantityMap.current = {};
                        reduxDispatch(
                            EntityFiltersActions.toggleFilters(
                                PRODUCTS,
                                false,
                                true,
                                PRODUCTS_FILTER_KEY,
                            ),
                        );
                        getFolders();
                    },
                });
        }, [getFolders, onRef, reduxDispatch, state.folderTree]);

        // Refresh table and count when folder, search or filters change
        useEffect(() => {
            if (!state.isOpen) return;
            tableRef.current?.refresh({ resetOffset: true });
        }, [state.currentFolder, state.searchText, filtersHash, state.isOpen]);

        useEffect(() => {
            return subscribe(UPDATE_PRODUCTS_MODAL_QUANTITY, (props) => {
                quantityMap.current = props.quantityMap.current;
            });
        }, []);

        const onConfirm = useCallback(() => {
            onAddProducts &&
                onAddProducts(
                    addedProducts.current.map((product) => ({
                        product,
                        quantity: quantityMap.current[product.id],
                    })),
                );
            onCancel();
        }, [onAddProducts, onCancel]);

        const onCancel = useCallback(() => {
            dispatch({ type: 'close' });
            setTimeout(() => dispatch({ type: 'reset' }), 500);
        }, []);

        const onClickFolder = useCallback(
            (item) =>
                dispatch({
                    type: 'setCurrentFolder',
                    id: item.id,
                }),
            [],
        );

        const onAddProduct = useCallback((items) => {
            addedProducts.current = items;
        }, []);

        const showContent = !state.isLoading && !state.error;

        const config = CONFIG();

        const filtersClasses = classnames('products-modal-filters-overlay', {
            'show-filters': showFilters,
        });

        return (
            <Modal
                overlayClassName="fm-add-products-modal__overlay"
                className="fm-add-products-modal"
                size="full"
                useCornerClose={false}
                isOpen={state.isOpen}
                onCancel={onCancel}
                onRequestClose={onCancel}
                onConfirm={onConfirm}
                confirmText={getLiteral('action_save')}
                cancelText={getLiteral('action_cancel')}
                title={getLiteral('label_add_lines')}
                useAutoHeight
                useContentStaticHeight
            >
                {state.isLoading && <AbsoluteLoader size="massive" />}
                {state.error && <AbsoluteError />}
                {showContent && (
                    <FiltersProvider>
                        <Advice
                            className="fm-add-products-modal__advice"
                            type="info"
                            showIcon
                            showCollapse={false}
                        >
                            {getLiteral('label_salesorders_add_products_advice')}
                        </Advice>
                        <DetailLayout
                            header={
                                <ProductsMenuHeader
                                    config={config}
                                    dispatch={dispatch}
                                    entity={PRODUCTS}
                                    filterKey={PRODUCTS_FILTER_KEY}
                                    searchText={state.searchText}
                                />
                            }
                            navigatorTitle={getLiteral('title_product_families')}
                            withUsageLimit={
                                <FreeTrialWarning
                                    entity={SALESORDERS}
                                    addonName="addon_salesorders"
                                />
                            }
                        >
                            <div
                                ref={contentContainerRef}
                                className="fm-add-products-modal__content"
                            >
                                <div
                                    ref={treeSidebarRef}
                                    className="fm-add-products-modal__content-tree"
                                    style={{ width: treeWidth }}
                                >
                                    <div className="fm-add-products-modal__tree-title">
                                        <Text type="subtitle">
                                            {getLiteral('label_products_tree_title')}
                                        </Text>
                                    </div>
                                    <div className="fm-add-products-modal__tree-scroll">
                                        <TreeHoi
                                            nodes={state.folderTree}
                                            onSelect={onClickFolder}
                                            canSelectParents
                                        />
                                    </div>
                                </div>
                                <AddProductsTable
                                    id="add-product-lines-table"
                                    className="fm-add-products-modal__table"
                                    isLoading={state.isLoadingProducts}
                                    hasError={state.errorProducts}
                                    currencySymbol={currencySymbol}
                                    idEnvironment={idEnvironment}
                                    idCurrency={idCurrency}
                                    quantityMap={quantityMap}
                                    onClickFolder={onClickFolder}
                                    useLazyLoad={true}
                                    initTable={initTable}
                                    getTotal={getTotal}
                                    pageSize={PAGE_SIZE}
                                    onRef={(ref) => {
                                        tableRef.current = ref;
                                    }}
                                />
                            </div>
                        </DetailLayout>
                        <Summary
                            currencySymbol={currencySymbol}
                            idEnvironment={idEnvironment}
                            idCurrency={idCurrency}
                            onAddProduct={onAddProduct}
                        />
                        <div className={filtersClasses}>
                            <div className="products-modal-filters-overlay__content">
                                <NewEntityFilters
                                    defaultFilters={config.defaultFilters}
                                    defaultQuickFilters={config.quickFilters}
                                    dontIgnoreTheseFilters={['onlySelected']}
                                    entity={PRODUCTS}
                                    filterKey={PRODUCTS_FILTER_KEY}
                                    filtersAddButtonSchema={config.filtersAddButtonSchema}
                                    useDrawer={config.useFiltersDrawer}
                                />
                            </div>
                        </div>
                    </FiltersProvider>
                )}
            </Modal>
        );
    },
);

export default AddProductsModal;
